import { EventBus } from "../events/EventBus";
import { uuid } from "../utils/Uuid";
import { ContextFlow } from "./ContextFlow";

// =============================================================================
// Base class for workflow steps
// =============================================================================

export abstract class StepFlow {
  protected _context!: ContextFlow;

  forceContext(context: ContextFlow): void {
    this._context = context;
  }

  getCtx<T>(key: string): T | undefined {
    return this._context.get<T>(key);
  }

  setCtx<T>(key: string, value: T): void {
    this._context.set(key, value);
  }
  public id: string;

  public isExclusive: boolean = false;
  protected eventBus: EventBus;
  protected cancelRequested: boolean = false;
  private eventSubscriptions: Array<{ off: () => void; }> = [];
  requiresReconciliation: boolean = false;
  private reconciliationState: Map<string, any> = new Map();
  // New flag to check if next() has been called
  public hasSignaledNext: boolean = false;
  get stepType() { return "Step"; }

  async reconcile(payload: any): Promise<boolean> {
    if (!this.requiresReconciliation) return true;

    const sourceId = payload.sourceStepId;
    this.reconciliationState.set(sourceId, payload);

    if (this.hasAllRequiredInputs()) {
      await this.onReconciledExecute();
      return true;
    }
    return false;
  }

  // Reconciliation controls
  protected reconciliationSources: string[] = [];
  protected receivedData: Map<string, any> = new Map();

  async processReconciliation(sourceStepName: string): Promise<boolean> {
    if (!this.requiresReconciliation) return true;
    console.log("rec test", sourceStepName)
    this.receivedData.set(sourceStepName, true);

    const allSourcesPresent = this.reconciliationSources.every(source => this.receivedData.has(source));

    console.log(this.stepName, allSourcesPresent, this.reconciliationSources, this.receivedData)

    if (allSourcesPresent) {
      await this.onReconciledExecute();
      return true;
    }
    return false;
  }

  protected async onReconciledExecute(): Promise<void> {
    // Default implementation can be empty
    console.log("Default")
    this.next();
  }

  protected hasAllRequiredInputs(): boolean {
    // Implementation depends on your reconciliation logic
    return this.reconciliationState.size >= 2;
  }


  // Each derived class must expose its step name.
  abstract get stepName(): string;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
    this.id = uuid();
  }

  async onInit(): Promise<void> {
    // Default implementation: do nothing.
  }

  async onEventSetup(): Promise<void> {
    // Default implementation: do nothing.
  }

  // Main execution logic; must eventually call next(), cancel(), or error().
  abstract onExecute(): Promise<void>;


  async onReconciliationExecute(): Promise<void> { }

  async onError(error: any): Promise<void> {
    console.error(`Error in Step ${this.stepName} (${this.id}):`, error);
  }

  async onCancel(): Promise<void> {
    // Default implementation: do nothing.
  }

  protected registerEventSubscription(sub: { off: () => void; }) {
    this.eventSubscriptions.push(sub);
  }

  async onEventTearDown(): Promise<void> {
    // Clean up all event subscriptions
    this.eventSubscriptions.forEach(sub => sub.off());
    this.eventSubscriptions = [];
  }

  // ===========================================================================
  // Cross-instance subscription tracking
  // ===========================================================================
  //
  // FlowRunner re-instantiates a step on every flow re-entry (back/forward,
  // refresh recovery, parallel sub-flow re-mount). The previous instance's
  // handlers stay attached to the global event bus even after this instance's
  // `onEventTearDown` runs — it only walks the per-instance array. Without a
  // cross-instance drain, a single event fan-outs to every accumulated handler
  // → duplicate work (e.g. the same POST issued N times on one click).
  //
  // Subclasses that need this guarantee should:
  //   1. Use `registerPersistentEventSubscription` instead of
  //      `registerEventSubscription`. This also pushes the subscription into a
  //      static, class-scoped registry.
  //   2. Call `await this.drainPersistentSubscriptions()` at the very top of
  //      `onEventSetup`. This `off()`s every prior-instance handler before new
  //      ones are registered.
  //
  // Per-subclass scoping is keyed on `this.stepName` — the abstract identifier
  // every subclass already declares (and that FlowRunner uses to route
  // next/error events). Using it instead of `this.constructor.name` makes the
  // registry robust to SWC/Terser class-name mangling in production builds:
  // `name` is not preserved by default in Next.js minified output, but
  // `stepName` is a string literal on the subclass and survives intact.

  private static persistentSubscriptions: Map<string, Array<{ off: () => void }>> =
    new Map();

  private getPersistentSubscriptionRegistry(): Array<{ off: () => void }> {
    const key = this.stepName;
    let arr = StepFlow.persistentSubscriptions.get(key);
    if (!arr) {
      arr = [];
      StepFlow.persistentSubscriptions.set(key, arr);
    }
    return arr;
  }

  /**
   * Register a subscription that should survive across step re-instantiations
   * for the purpose of cross-instance teardown. The subscription is added to
   * BOTH the per-instance array (so `onEventTearDown` still drains it on
   * normal exit) AND a per-subclass static registry (so a fresh instance can
   * drain it via `drainPersistentSubscriptions`).
   */
  protected registerPersistentEventSubscription(sub: { off: () => void }): void {
    this.registerEventSubscription(sub);
    this.getPersistentSubscriptionRegistry().push(sub);
  }

  /**
   * Drain every prior-instance subscription registered via
   * `registerPersistentEventSubscription` for this subclass. Safe to call
   * even when no prior instance exists; each `off()` is wrapped in a try
   * so an already-removed handler does not abort the drain. Also runs
   * `onEventTearDown` for the same-instance resume case (a flow resume
   * that re-enters `onEventSetup` on the same instance).
   */
  protected async drainPersistentSubscriptions(): Promise<void> {
    const arr = this.getPersistentSubscriptionRegistry();
    for (const sub of arr) {
      try {
        sub.off();
      } catch {
        // off() throwing means the handler was already removed (e.g. by
        // a same-instance onEventTearDown earlier in the lifecycle).
        // Safe to ignore — the goal is "no duplicate dispatch".
      }
    }
    arr.length = 0;
    await this.onEventTearDown();
  }

  async onExit(): Promise<void> {
    // Default implementation: do nothing.
  }

  async onPause(): Promise<void> {
    // Default implementation: do nothing.
  }

  async onResume(): Promise<void> {
    // Default implementation: do nothing.
  }

  next(): void {
    this.hasSignaledNext = true;
    this.eventBus.emit({
      eventName: `step:${this.stepName}:${this.id}:next`,
      data: { stepId: this.id, timestamp: new Date().toISOString() }
    });
  }

  error(payload: any): void {
    this.eventBus.emit({
      eventName: `step:${this.stepName}:${this.id}:error`,
      data: { error: payload, timestamp: new Date().toISOString() }
    });
  }

  cancel(): void {
    this.cancelRequested = true;
    this.eventBus.emit({
      eventName: `step:cancel`,
      data: { stepId: this.id, timestamp: new Date().toISOString() }
    });
  }

  exitFlow(): void {
    this.cancelRequested = true;
    this.eventBus.emit({
      eventName: `step:exitFlow`,
      data: { stepId: this.id, timestamp: new Date().toISOString() }
    });
  }
}